import { useState, FormEvent } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    visitDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('visitDate', formData.visitDate);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6fhdbaau1b90a404080', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          visitDate: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-[#00A58E] to-[#008C7A] relative overflow-hidden">
      {/* Decorative map background */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 1000 1000">
          <path d="M100,200 Q200,100 300,200 T500,200 T700,200 T900,200" stroke="white" fill="none" strokeWidth="2"/>
          <path d="M100,400 Q200,300 300,400 T500,400 T700,400 T900,400" stroke="white" fill="none" strokeWidth="2"/>
          <path d="M100,600 Q200,500 300,600 T500,600 T700,600 T900,600" stroke="white" fill="none" strokeWidth="2"/>
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          {/* Left: Text Area (2 columns) */}
          <div className="lg:col-span-2 text-white space-y-8">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://static.readdy.ai/image/aec91cb01fdf30f00658c017aec0d8c9/bac9cc4b08f51c73312a598e4205cf6d.png" 
                alt="飛翠ロゴ" 
                className="h-16 w-auto brightness-0 invert"
              />
            </div>

            <div className="space-y-4">
              <p className="text-2xl font-light">まずはお気軽に、</p>
              <h2 className="text-6xl font-bold">無料相談から</h2>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 space-y-4">
              <p className="text-xl font-semibold">お電話でのお問い合わせも大歓迎です</p>
              <a href="tel:080-7347-9482" className="text-4xl font-bold hover:text-[#FFB6C1] transition-colors block">
                080-7347-9482
              </a>
              <p className="text-sm opacity-90">受付時間：9:00〜18:00（年中無休）</p>
            </div>

            <div className="space-y-3 pt-4">
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                <span className="text-lg">出張費・査定費 完全無料</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                <span className="text-lg">24時間以内に返信</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                <span className="text-lg">しつこい営業は一切なし</span>
              </div>
            </div>
          </div>

          {/* Right: Form Area (3 columns) */}
          <div className="lg:col-span-3">
            <form 
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-10 shadow-2xl space-y-6"
            >
              <h3 className="text-3xl font-bold text-[#3E2723] mb-6">
                お問い合わせフォーム
              </h3>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center gap-2 bg-[#E0F2F1] px-4 py-2 rounded-full">
                  <i className="ri-shield-check-line text-[#00A58E]"></i>
                  <span className="text-sm font-semibold text-[#00A58E]">個人情報厳守</span>
                </div>
                <div className="flex items-center gap-2 bg-[#FFF3E0] px-4 py-2 rounded-full">
                  <i className="ri-close-circle-line text-[#FF6B35]"></i>
                  <span className="text-sm font-semibold text-[#FF6B35]">しつこい営業なし</span>
                </div>
                <div className="flex items-center gap-2 bg-[#E8F5E9] px-4 py-2 rounded-full">
                  <i className="ri-time-line text-[#4CAF50]"></i>
                  <span className="text-sm font-semibold text-[#4CAF50]">24時間以内に返信</span>
                </div>
              </div>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-[#00A58E] focus:ring-2 focus:ring-[#00A58E]/20 outline-none transition-all text-base"
                  placeholder="山田 太郎"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  お電話番号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-[#00A58E] focus:ring-2 focus:ring-[#00A58E]/20 outline-none transition-all text-base"
                  placeholder="090-1234-5678"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-[#00A58E] focus:ring-2 focus:ring-[#00A58E]/20 outline-none transition-all text-base"
                  placeholder="example@email.com"
                />
              </div>

              {/* Visit Date */}
              <div>
                <label htmlFor="visitDate" className="block text-sm font-semibold text-gray-700 mb-2">
                  ご希望の訪問日時
                </label>
                <input
                  type="text"
                  id="visitDate"
                  name="visitDate"
                  value={formData.visitDate}
                  onChange={(e) => setFormData({...formData, visitDate: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-[#00A58E] focus:ring-2 focus:ring-[#00A58E]/20 outline-none transition-all text-base"
                  placeholder="例：2025年2月15日 午後2時頃"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  お問い合わせ内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-[#00A58E] focus:ring-2 focus:ring-[#00A58E]/20 outline-none transition-all resize-none text-base"
                  placeholder="買取希望の品物や、ご質問などをお書きください（500文字以内）"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white py-4 rounded-full text-lg font-bold hover:shadow-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? '送信中...' : '無料相談を申し込む'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-check-line mr-2"></i>
                  お問い合わせありがとうございます。24時間以内にご連絡いたします。
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  送信に失敗しました。お手数ですが、もう一度お試しください。
                </div>
              )}

              <p className="text-center text-sm text-gray-600">
                <i className="ri-lock-line mr-1"></i>
                お客様の個人情報は厳重に管理いたします
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}